import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Plus,
  Search,
  Columns3,
  AlertTriangle,
  Eye,
  Phone,
  Mail,
  User,
  Scale as LegaliLogoIcon
} from 'lucide-react';
import api from '../services/api';

const StyledCard = styled(Card)(() => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
}));

export default function Legali() {
  const columnDefs = [
    { key: 'id', label: 'ID', sortable: true },
    { key: 'nome', label: 'Legale', sortable: true },
    { key: 'cognome', label: 'Cognome', sortable: true },
    { key: 'email', label: 'Email', sortable: true },
    { key: 'pec', label: 'PEC', sortable: true },
    { key: 'telefono', label: 'Telefono', sortable: true },
    { key: 'codice_fiscale', label: 'Codice fiscale', sortable: true },
    { key: 'partita_iva', label: 'Partita IVA', sortable: true },
    { key: 'foro', label: 'Foro', sortable: true },
    { key: 'numero_albo', label: 'Numero albo', sortable: true },
    { key: 'pratiche_totale', label: 'Totale Pratiche', sortable: true },
    { key: 'pratiche_percentuale', label: '% sul totale', sortable: true }
  ];
  const interniColumnDefs = [
    { key: 'name', label: 'Legale interno', sortable: true },
    { key: 'email', label: 'Email', sortable: true },
    { key: 'pratiche_totale', label: 'Totale Pratiche', sortable: true },
    { key: 'pratiche_percentuale', label: '% sul totale', sortable: true }
  ];

  const [rows, setRows] = useState([]);
  const [interniRows, setInterniRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [interniLoading, setInterniLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('esterni');
  const [search, setSearch] = useState('');
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('cognome');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState([
    'nome',
    'codice_fiscale',
    'partita_iva',
    'foro',
    'numero_albo',
    'pratiche_totale',
    'pratiche_percentuale'
  ]);
  const [saving, setSaving] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [praticheDialogOpen, setPraticheDialogOpen] = useState(false);
  const [praticheLoading, setPraticheLoading] = useState(false);
  const [praticheRows, setPraticheRows] = useState([]);
  const [praticheLegale, setPraticheLegale] = useState(null);
  const [praticheOrderBy, setPraticheOrderBy] = useState('updated_at');
  const [praticheOrder, setPraticheOrder] = useState('desc');
  const [pratichePage, setPratichePage] = useState(0);
  const [praticheRowsPerPage, setPraticheRowsPerPage] = useState(10);

  const [nome, setNome] = useState('');
  const [cognome, setCognome] = useState('');
  const [email, setEmail] = useState('');
  const [pec, setPec] = useState('');
  const [telefono, setTelefono] = useState('');
  const [codiceFiscale, setCodiceFiscale] = useState('');
  const [partitaIva, setPartitaIva] = useState('');
  const [foro, setForo] = useState('');
  const [numeroAlbo, setNumeroAlbo] = useState('');

  const fetchRows = async () => {
    try {
      setLoading(true);
      const res = await api.get('/legali');
      setRows(res.data || []);
    } catch (error) {
      console.error('Error fetching legali:', error);
      toast.error('Errore nel caricamento dei legali');
    } finally {
      setLoading(false);
    }
  };

  const fetchInterniRows = async () => {
    try {
      setInterniLoading(true);
      const res = await api.get('/legali/interni');
      setInterniRows(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Error fetching legali interni con pratiche:', error);
      toast.error('Errore nel caricamento dei legali interni');
    } finally {
      setInterniLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    fetchInterniRows();
  }, []);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) =>
      [
        row.nome,
        row.cognome,
        row.email,
        row.pec,
        row.telefono,
        row.codice_fiscale,
        row.partita_iva,
        row.foro,
        row.numero_albo
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(term)
    );
  }, [rows, search]);

  const filteredInterniRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return interniRows;
    return interniRows.filter((row) =>
      [row.name, row.email]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(term)
    );
  }, [interniRows, search]);

  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];
    arr.sort((a, b) => {
      const valueA = a[orderBy];
      const valueB = b[orderBy];

      if (valueA === null || valueA === undefined) return 1;
      if (valueB === null || valueB === undefined) return -1;

      if (orderBy === 'id' || orderBy === 'pratiche_totale' || orderBy === 'pratiche_percentuale') {
        return order === 'asc' ? Number(valueA) - Number(valueB) : Number(valueB) - Number(valueA);
      }

      const strA = String(valueA).toLowerCase();
      const strB = String(valueB).toLowerCase();
      if (strA < strB) return order === 'asc' ? -1 : 1;
      if (strA > strB) return order === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filteredRows, order, orderBy]);

  const sortedInterniRows = useMemo(() => {
    const arr = [...filteredInterniRows];
    arr.sort((a, b) => {
      const valueA = a[orderBy];
      const valueB = b[orderBy];

      if (valueA === null || valueA === undefined) return 1;
      if (valueB === null || valueB === undefined) return -1;

      if (orderBy === 'id' || orderBy === 'pratiche_totale' || orderBy === 'pratiche_percentuale') {
        return order === 'asc' ? Number(valueA) - Number(valueB) : Number(valueB) - Number(valueA);
      }

      const strA = String(valueA).toLowerCase();
      const strB = String(valueB).toLowerCase();
      if (strA < strB) return order === 'asc' ? -1 : 1;
      if (strA > strB) return order === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filteredInterniRows, order, orderBy]);

  const displayedColumns = useMemo(
    () => columnDefs.filter((col) => visibleColumns.includes(col.key)),
    [visibleColumns]
  );
  const displayedInterniColumns = interniColumnDefs;

  const handleToggleColumn = (columnKey) => {
    setVisibleColumns((prev) => {
      const isVisible = prev.includes(columnKey);
      if (isVisible && prev.length === 1) {
        return prev;
      }
      if (isVisible) {
        return prev.filter((key) => key !== columnKey);
      }
      return [...prev, columnKey];
    });
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeTab = (_event, nextTab) => {
    setActiveTab(nextTab);
    setSearch('');
    setPage(0);
    if (nextTab === 'interni') {
      setOrderBy('pratiche_totale');
      setOrder('desc');
      return;
    }
    setOrderBy('cognome');
    setOrder('asc');
  };

  const resetForm = () => {
    setNome('');
    setCognome('');
    setEmail('');
    setPec('');
    setTelefono('');
    setCodiceFiscale('');
    setPartitaIva('');
    setForo('');
    setNumeroAlbo('');
  };

  const openCreateDialog = () => {
    setEditingItem(null);
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setNome(item.nome || '');
    setCognome(item.cognome || '');
    setEmail(item.email || '');
    setPec(item.pec || '');
    setTelefono(item.telefono || '');
    setCodiceFiscale(item.codice_fiscale || '');
    setPartitaIva(item.partita_iva || '');
    setForo(item.foro || '');
    setNumeroAlbo(item.numero_albo || '');
    setDialogOpen(true);
  };

  const openDeleteDialog = (item) => {
    setDeleteTarget(item);
    setDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    if (!nome.trim()) {
      toast.error('Il nome è obbligatorio');
      return;
    }
    if (!cognome.trim()) {
      toast.error('Il cognome è obbligatorio');
      return;
    }

    const payload = {
      nome: nome.trim(),
      cognome: cognome.trim(),
      email: email.trim() || null,
      pec: pec.trim() || null,
      telefono: telefono.trim() || null,
      codice_fiscale: codiceFiscale.trim().toUpperCase() || null,
      partita_iva: partitaIva.trim() || null,
      foro: foro.trim() || null,
      numero_albo: numeroAlbo.trim() || null
    };

    try {
      setSaving(true);
      if (editingItem?.id) {
        await api.put(`/legali/${editingItem.id}`, payload);
        toast.success('Legale aggiornato');
      } else {
        await api.post('/legali', payload);
        toast.success('Legale creato');
      }
      setDialogOpen(false);
      await fetchRows();
    } catch (error) {
      console.error('Error saving legale:', error);
      const message = error.response?.data?.message || 'Errore nel salvataggio del legale';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/legali/${deleteTarget.id}`);
      toast.success('Legale eliminato');
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      await fetchRows();
    } catch (error) {
      console.error('Error deleting legale:', error);
      const message = error.response?.data?.message || 'Errore nell\'eliminazione del legale';
      toast.error(message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '-';
    return parsed.toLocaleDateString('it-IT');
  };

  const openPraticheDialog = async (item, referenteTipo = 'esterno') => {
    if (!item?.id) return;
    setPraticheDialogOpen(true);
    setPraticheLegale(item);
    setPraticheRows([]);
    setPraticheOrderBy('updated_at');
    setPraticheOrder('desc');
    setPratichePage(0);
    setPraticheRowsPerPage(10);
    try {
      setPraticheLoading(true);
      const endpoint =
        referenteTipo === 'interno' ? `/legali/interni/${item.id}/pratiche` : `/legali/${item.id}/pratiche`;
      const res = await api.get(endpoint);
      setPraticheRows(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Error fetching pratiche legale:', error);
      toast.error('Errore nel caricamento delle pratiche del legale');
    } finally {
      setPraticheLoading(false);
    }
  };

  const sortedPraticheRows = useMemo(() => {
    const arr = [...praticheRows];
    const numericFields = new Set(['numero_rg', 'anno_rg', 'id']);
    const dateFields = new Set(['data_scadenza', 'updated_at']);

    arr.sort((a, b) => {
      const valueA = a?.[praticheOrderBy];
      const valueB = b?.[praticheOrderBy];

      if (valueA === null || valueA === undefined) return 1;
      if (valueB === null || valueB === undefined) return -1;

      if (dateFields.has(praticheOrderBy)) {
        const timeA = new Date(valueA).getTime();
        const timeB = new Date(valueB).getTime();
        const safeA = Number.isNaN(timeA) ? 0 : timeA;
        const safeB = Number.isNaN(timeB) ? 0 : timeB;
        return praticheOrder === 'asc' ? safeA - safeB : safeB - safeA;
      }

      if (numericFields.has(praticheOrderBy)) {
        const numA = Number(valueA || 0);
        const numB = Number(valueB || 0);
        return praticheOrder === 'asc' ? numA - numB : numB - numA;
      }

      const strA = String(valueA || '').toLowerCase();
      const strB = String(valueB || '').toLowerCase();
      if (strA < strB) return praticheOrder === 'asc' ? -1 : 1;
      if (strA > strB) return praticheOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return arr;
  }, [praticheRows, praticheOrderBy, praticheOrder]);

  const pagedPraticheRows = useMemo(
    () =>
      sortedPraticheRows.slice(
        pratichePage * praticheRowsPerPage,
        pratichePage * praticheRowsPerPage + praticheRowsPerPage
      ),
    [sortedPraticheRows, pratichePage, praticheRowsPerPage]
  );

  const handlePraticheRequestSort = (property) => {
    const isAsc = praticheOrderBy === property && praticheOrder === 'asc';
    setPraticheOrder(isAsc ? 'desc' : 'asc');
    setPraticheOrderBy(property);
    setPratichePage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <LegaliLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
              Legali
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gestione anagrafica degli avvocati/legali
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<Plus size={18} />} onClick={openCreateDialog}>
          Nuovo legale
        </Button>
      </Box>

      <Menu
        anchorEl={columnsMenuAnchor}
        open={Boolean(columnsMenuAnchor)}
        onClose={() => setColumnsMenuAnchor(null)}
      >
        {columnDefs.map((column) => {
          const checked = visibleColumns.includes(column.key);
          const disableHide = checked && visibleColumns.length === 1;
          return (
            <MenuItem
              key={column.key}
              dense
              onClick={() => handleToggleColumn(column.key)}
              disabled={disableHide}
            >
              <Checkbox size="small" checked={checked} />
              <ListItemText primary={column.label} />
            </MenuItem>
          );
        })}
      </Menu>

      <StyledCard sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleChangeTab}
          sx={{ px: 2, pt: 1, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <Tab value="esterni" label="Legali" />
          <Tab value="interni" label="Legali (interni)" />
        </Tabs>
      </StyledCard>

      <StyledCard>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            placeholder={
              activeTab === 'esterni'
                ? 'Cerca per nome, cognome, email, pec, telefono, codice fiscale, partita IVA, foro...'
                : 'Cerca per nominativo o email del legale interno...'
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} />
                </InputAdornment>
              )
            }}
            sx={{ maxWidth: 420 }}
          />
          {activeTab === 'esterni' && (
            <Tooltip title="Colonne">
              <IconButton onClick={(e) => setColumnsMenuAnchor(e.currentTarget)}>
                <Columns3 size={18} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.neutral' }}>
                {activeTab === 'esterni'
                  ? displayedColumns.map((column) => (
                      <TableCell
                        key={column.key}
                        align={column.key === 'pratiche_totale' || column.key === 'pratiche_percentuale' ? 'right' : 'left'}
                        sortDirection={column.sortable && orderBy === column.key ? order : false}
                      >
                        {column.sortable ? (
                          <TableSortLabel
                            active={orderBy === column.key}
                            direction={orderBy === column.key ? order : 'asc'}
                            onClick={() => handleRequestSort(column.key)}
                          >
                            {column.label}
                          </TableSortLabel>
                        ) : (
                          column.label
                        )}
                      </TableCell>
                    ))
                  : displayedInterniColumns.map((column) => (
                      <TableCell
                        key={column.key}
                        align={column.key === 'pratiche_totale' || column.key === 'pratiche_percentuale' ? 'right' : 'left'}
                        sortDirection={column.sortable && orderBy === column.key ? order : false}
                      >
                        <TableSortLabel
                          active={orderBy === column.key}
                          direction={orderBy === column.key ? order : 'asc'}
                          onClick={() => handleRequestSort(column.key)}
                        >
                          {column.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {(activeTab === 'esterni' ? loading : interniLoading) ? (
                <TableRow>
                  <TableCell
                    colSpan={activeTab === 'esterni' ? displayedColumns.length + 1 : displayedInterniColumns.length + 1}
                    align="center"
                    sx={{ py: 3 }}
                  >
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : (activeTab === 'esterni' ? sortedRows.length : sortedInterniRows.length) === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={activeTab === 'esterni' ? displayedColumns.length + 1 : displayedInterniColumns.length + 1}
                    align="center"
                    sx={{ py: 3 }}
                  >
                    <Typography variant="body1" color="text.secondary">
                      {activeTab === 'esterni'
                        ? 'Nessun legale presente'
                        : 'Nessun legale interno associato a pratiche'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : activeTab === 'esterni' ? (
                sortedRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow key={row.id} hover>
                      {displayedColumns.map((column) => (
                        <TableCell
                          key={`${row.id}-${column.key}`}
                          align={column.key === 'pratiche_totale' || column.key === 'pratiche_percentuale' ? 'right' : 'left'}
                          sx={column.key === 'nome' ? { minWidth: 360 } : {}}
                        >
                          {column.key === 'nome' ? (
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                              <Box
                                sx={{
                                  width: 38,
                                  height: 38,
                                  borderRadius: '50%',
                                  bgcolor: '#eef2ff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  mt: 0.2
                                }}
                              >
                                <User size={18} color="#2563eb" />
                              </Box>
                              <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                                  {`${row.nome || ''} ${row.cognome || ''}`.trim() || '-'}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.35 }}>
                                  <Phone size={14} color="#64748b" />
                                  <Typography variant="body2" color="text.secondary">
                                    {row.telefono || '-'}
                                  </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                  {row.email || '-'}
                                </Typography>
                              </Box>
                            </Box>
                          ) : column.key === 'pratiche_totale' ? (
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                                border: '1px solid rgba(245, 158, 11, 0.35)',
                                backgroundColor: 'rgba(245, 158, 11, 0.04)',
                                minWidth: 56
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#F59E0B', letterSpacing: 0.2 }}>
                                {Number(row.pratiche_totale) || 0}
                              </Typography>
                            </Box>
                          ) : column.key === 'pratiche_percentuale' ? (
                            (() => {
                              const rawPercent = Number(row.pratiche_percentuale);
                              const safePercent = Number.isFinite(rawPercent) ? rawPercent : 0;
                              const clampedPercent = Math.max(0, Math.min(100, safePercent));
                              return (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                    <CircularProgress
                                      variant="determinate"
                                      value={100}
                                      size={36}
                                      thickness={6}
                                      sx={{ color: 'rgba(145, 158, 171, 0.24)' }}
                                    />
                                    <CircularProgress
                                      variant="determinate"
                                      value={clampedPercent}
                                      size={36}
                                      thickness={6}
                                      sx={{ position: 'absolute', left: 0 }}
                                      color="warning"
                                    />
                                    <Box
                                      sx={{
                                        top: 0,
                                        left: 0,
                                        bottom: 0,
                                        right: 0,
                                        position: 'absolute',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                    >
                                      <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.primary', fontSize: '0.7rem' }}>
                                        {safePercent.toFixed(0)}%
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Box>
                              );
                            })()
                          ) : (
                            row[column.key] || '-'
                          )}
                        </TableCell>
                      ))}
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5 }}>
                          <Tooltip title="Visualizza pratiche">
                            <IconButton color="info" onClick={() => openPraticheDialog(row)}>
                              <Eye size={18} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Modifica">
                            <IconButton color="primary" onClick={() => openEditDialog(row)}>
                              <EditIcon size={18} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Elimina">
                            <IconButton color="error" onClick={() => openDeleteDialog(row)}>
                              <DeleteIcon size={18} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
              ) : (
                sortedInterniRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow key={row.id} hover>
                      {displayedInterniColumns.map((column) => (
                        <TableCell
                          key={`${row.id}-${column.key}`}
                          align={column.key === 'pratiche_totale' || column.key === 'pratiche_percentuale' ? 'right' : 'left'}
                          sx={column.key === 'name' ? { minWidth: 360 } : {}}
                        >
                          {column.key === 'name' ? (
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                              <Box
                                sx={{
                                  width: 38,
                                  height: 38,
                                  borderRadius: '50%',
                                  bgcolor: '#eef2ff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  mt: 0.2
                                }}
                              >
                                <User size={18} color="#2563eb" />
                              </Box>
                              <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                                  {row.name || '-'}
                                </Typography>
                              </Box>
                            </Box>
                          ) : column.key === 'pratiche_totale' ? (
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                                border: '1px solid rgba(245, 158, 11, 0.35)',
                                backgroundColor: 'rgba(245, 158, 11, 0.04)',
                                minWidth: 56
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#F59E0B', letterSpacing: 0.2 }}>
                                {Number(row.pratiche_totale) || 0}
                              </Typography>
                            </Box>
                          ) : column.key === 'pratiche_percentuale' ? (
                            (() => {
                              const rawPercent = Number(row.pratiche_percentuale);
                              const safePercent = Number.isFinite(rawPercent) ? rawPercent : 0;
                              const clampedPercent = Math.max(0, Math.min(100, safePercent));
                              return (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                    <CircularProgress
                                      variant="determinate"
                                      value={100}
                                      size={36}
                                      thickness={6}
                                      sx={{ color: 'rgba(145, 158, 171, 0.24)' }}
                                    />
                                    <CircularProgress
                                      variant="determinate"
                                      value={clampedPercent}
                                      size={36}
                                      thickness={6}
                                      sx={{ position: 'absolute', left: 0 }}
                                      color="warning"
                                    />
                                    <Box
                                      sx={{
                                        top: 0,
                                        left: 0,
                                        bottom: 0,
                                        right: 0,
                                        position: 'absolute',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                    >
                                      <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.primary', fontSize: '0.7rem' }}>
                                        {safePercent.toFixed(0)}%
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Box>
                              );
                            })()
                          ) : (
                            row[column.key] || '-'
                          )}
                        </TableCell>
                      ))}
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="Visualizza pratiche">
                          <IconButton color="info" onClick={() => openPraticheDialog(row, 'interno')}>
                            <Eye size={18} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={activeTab === 'esterni' ? sortedRows.length : sortedInterniRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingItem ? 'Modifica legale' : 'Nuovo legale'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Nome"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                fullWidth
                required
                autoFocus
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Cognome"
                value={cognome}
                onChange={(e) => setCognome(e.target.value)}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="PEC"
                value={pec}
                onChange={(e) => setPec(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Telefono"
                value={telefono}
                onChange={(e) => setTelefono(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Codice fiscale"
                value={codiceFiscale}
                onChange={(e) => setCodiceFiscale(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Partita IVA"
                value={partitaIva}
                onChange={(e) => setPartitaIva(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Foro"
                value={foro}
                onChange={(e) => setForo(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Numero albo"
                value={numeroAlbo}
                onChange={(e) => setNumeroAlbo(e.target.value)}
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Annulla
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Salvataggio...' : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={praticheDialogOpen}
        onClose={() => {
          setPraticheDialogOpen(false);
          setPraticheRows([]);
          setPraticheLegale(null);
          setPraticheLoading(false);
          setPraticheOrderBy('updated_at');
          setPraticheOrder('desc');
          setPratichePage(0);
          setPraticheRowsPerPage(10);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Pratiche riferite a{' '}
          {praticheLegale
            ? `${praticheLegale.nome || praticheLegale.name || ''} ${praticheLegale.cognome || ''}`.trim()
            : 'legale'}
        </DialogTitle>
        <DialogContent dividers>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={praticheOrderBy === 'numero_rg' ? praticheOrder : false}>
                    <TableSortLabel
                      active={praticheOrderBy === 'numero_rg'}
                      direction={praticheOrderBy === 'numero_rg' ? praticheOrder : 'asc'}
                      onClick={() => handlePraticheRequestSort('numero_rg')}
                    >
                      Numero R.G.
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={praticheOrderBy === 'stato_pratica' ? praticheOrder : false}>
                    <TableSortLabel
                      active={praticheOrderBy === 'stato_pratica'}
                      direction={praticheOrderBy === 'stato_pratica' ? praticheOrder : 'asc'}
                      onClick={() => handlePraticheRequestSort('stato_pratica')}
                    >
                      Stato
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={praticheOrderBy === 'tribunale_nome' ? praticheOrder : false}>
                    <TableSortLabel
                      active={praticheOrderBy === 'tribunale_nome'}
                      direction={praticheOrderBy === 'tribunale_nome' ? praticheOrder : 'asc'}
                      onClick={() => handlePraticheRequestSort('tribunale_nome')}
                    >
                      Tribunale
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={praticheOrderBy === 'cliente_nome' ? praticheOrder : false}>
                    <TableSortLabel
                      active={praticheOrderBy === 'cliente_nome'}
                      direction={praticheOrderBy === 'cliente_nome' ? praticheOrder : 'asc'}
                      onClick={() => handlePraticheRequestSort('cliente_nome')}
                    >
                      Cliente
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={praticheOrderBy === 'controparte_nome' ? praticheOrder : false}>
                    <TableSortLabel
                      active={praticheOrderBy === 'controparte_nome'}
                      direction={praticheOrderBy === 'controparte_nome' ? praticheOrder : 'asc'}
                      onClick={() => handlePraticheRequestSort('controparte_nome')}
                    >
                      Controparte
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={praticheOrderBy === 'data_scadenza' ? praticheOrder : false}>
                    <TableSortLabel
                      active={praticheOrderBy === 'data_scadenza'}
                      direction={praticheOrderBy === 'data_scadenza' ? praticheOrder : 'asc'}
                      onClick={() => handlePraticheRequestSort('data_scadenza')}
                    >
                      Scadenza
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={praticheOrderBy === 'updated_at' ? praticheOrder : false}>
                    <TableSortLabel
                      active={praticheOrderBy === 'updated_at'}
                      direction={praticheOrderBy === 'updated_at' ? praticheOrder : 'asc'}
                      onClick={() => handlePraticheRequestSort('updated_at')}
                    >
                      Aggiornata il
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {praticheLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : praticheRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        Nessuna pratica collegata a questo legale
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  pagedPraticheRows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        {row.numero_rg || row.anno_rg ? `${row.numero_rg || '-'}${row.anno_rg ? ` / ${row.anno_rg}` : ''}` : '-'}
                      </TableCell>
                      <TableCell>{row.stato_pratica || '-'}</TableCell>
                      <TableCell>{row.tribunale_nome || '-'}</TableCell>
                      <TableCell sx={{ minWidth: 240 }}>
                        <Box sx={{ minWidth: 210, display: 'grid', gap: 0.2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <User size={14} color="#2563eb" />
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {row.cliente_nome || '-'}
                            </Typography>
                          </Box>
                          {row.cliente_telefono && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Phone size={13} color="#64748b" />
                              <Typography variant="caption" color="text.secondary">
                                {row.cliente_telefono}
                              </Typography>
                            </Box>
                          )}
                          {row.cliente_email && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Mail size={13} color="#64748b" />
                              <Typography variant="caption" color="text.secondary">
                                {row.cliente_email}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ minWidth: 320 }}>
                        <Box sx={{ minWidth: 280, display: 'grid', gap: 0.25 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {row.controparte_nome || '-'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Creditore principale: {row.creditore_principale_nome || '-'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Posizioni credito: {row.tipi_posizione_credito_label || '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{formatDate(row.data_scadenza)}</TableCell>
                      <TableCell>{formatDate(row.updated_at)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {!praticheLoading && praticheRows.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={sortedPraticheRows.length}
              rowsPerPage={praticheRowsPerPage}
              page={pratichePage}
              onPageChange={(_event, newPage) => setPratichePage(newPage)}
              onRowsPerPageChange={(event) => {
                setPraticheRowsPerPage(parseInt(event.target.value, 10));
                setPratichePage(0);
              }}
              labelRowsPerPage="Righe per pagina:"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPraticheDialogOpen(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={(_, reason) => {
          if (deleteLoading) return;
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          setDeleteDialogOpen(false);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                bgcolor: 'error.lighter',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <AlertTriangle size={16} color="#b71d18" />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Conferma eliminazione
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Stai per eliminare definitivamente il legale:
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {deleteTarget ? `${deleteTarget.nome || ''} ${deleteTarget.cognome || ''}`.trim() : '-'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Questa operazione non può essere annullata.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            No, annulla
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleteLoading}>
            {deleteLoading ? 'Eliminazione...' : 'Sì, elimina'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
